import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { OnboardingHeader } from "../components/onboarding/OnboardingHeader";
import { ProgressBar } from "../components/onboarding/ProgressBar";
import { useUserData } from "../contexts/user-data-context";
import { useAnalytics } from "../lib/analytics";
import { useClientTranslations } from "../lib/i18n/client";
import * as OnboardingService from "../services/onboardingService";
import styles from "./ThankYou.module.css";

export function ThankYou() {
	const navigate = useNavigate();
	const { t, isRTL } = useClientTranslations("onboarding");
	const { logEvent } = useAnalytics();
	const { refetchUserData } = useUserData();

	useEffect(() => {
		let hasRedirected = false;

		// Log screen view
		logEvent("onboarding_final_screen", undefined, {
			auto_redirect_enabled: "true",
			redirect_delay_seconds: "5",
		});

		const completeOnboardingAndRedirect = async () => {
			try {
				// Call complete onboarding API first
				logEvent("onboarding_completion_started", undefined, {
					source: "thank_you_screen",
				});

				// Mark user as b2c type when completing onboarding
				await OnboardingService.completeOnboarding({ userType: "b2c" });

				// Refresh user data to get updated fertility journey types
				await refetchUserData();

				logEvent("onboarding_completed", undefined, {
					source: "thank_you_screen",
					success: "true",
				});

				// After successful completion, set up automatic redirection after 5 seconds
				const redirectTimer = setTimeout(() => {
					logEvent("redirect_to_home_page", undefined, {
						source: "thank_you_screen",
						redirect_delay_seconds: "5",
						completion_status: "success",
					});

					hasRedirected = true;
					// Navigate to chat page
					navigate("/chat", { replace: true });
				}, 5000); // 5 seconds

				return () => {
					clearTimeout(redirectTimer);
				};
			} catch (error) {
				console.error("Error completing onboarding:", error);
				logEvent("onboarding_completed", undefined, {
					source: "thank_you_screen",
					success: "false",
					error_message: error instanceof Error ? error.message : "unknown",
				});

				// Even on error, redirect after 5 seconds
				const redirectTimer = setTimeout(() => {
					logEvent("redirect_to_home_page", undefined, {
						source: "thank_you_screen",
						redirect_delay_seconds: "5",
						completion_status: "error",
					});

					hasRedirected = true;
					navigate("/chat", { replace: true });
				}, 5000);

				return () => {
					clearTimeout(redirectTimer);
				};
			}
		};

		const cleanup = completeOnboardingAndRedirect();

		// Safety check: if redirect hasn't happened after 10 seconds, log it
		const redirectFailureTimer = setTimeout(() => {
			if (!hasRedirected) {
				logEvent("redirect_to_home_page_failed", undefined, {
					source: "thank_you_screen",
					expected_delay_seconds: "5",
					actual_delay_seconds: "10",
					reason: "timeout_exceeded",
				});
			}
		}, 10000); // 10 seconds

		return () => {
			clearTimeout(redirectFailureTimer);
			cleanup.then((cleanupFn) => {
				if (cleanupFn) cleanupFn();
			});
		};
	}, [navigate, logEvent, refetchUserData]);

	return (
		<div className={styles.container} dir={isRTL ? "rtl" : "ltr"}>
			{/* Header with logo and menu */}
			<div className={styles.headerContainer}>
				<OnboardingHeader showBackButton={false} />
			</div>

			{/* Progress bar at top showing 100% completion */}
			<div className={styles.progressBarContainer}>
				<ProgressBar currentIndex={8} total={8} questionType="thank-you" />
			</div>

			{/* Main content centered */}
			<div className={styles.content}>
				<div className={styles.textContent}>
					{/* Headers */}
					<div className={styles.headers}>
						{/* Title */}
						<h1 className={styles.title}>{t("thankYou.title")}</h1>

						{/* Subtitle */}
						<p className={styles.subtitle}>{t("thankYou.subtitle")}</p>
					</div>

					{/* Illustration */}
					<div className={styles.illustrationContainer}>
						<img
							src="/images/thank-you.png"
							alt="Welcome"
							className={styles.illustration}
						/>
					</div>

					{/* Journey ready text */}
					<p className={styles.journeyText}>{t("thankYou.journeyReady")}</p>
				</div>
			</div>
		</div>
	);
}
